import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, EventEmitter, HostListener, inject, input, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { parse } from 'mathjs';
import * as moment from 'moment';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import Quill from 'quill';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { QuillToolbarComponent } from 'src/app/shared/components/quill-toolbar/quill-toolbar.component';
import { COLOR_WHITELIST } from 'src/app/shared/components/quill-toolbar/quill-toolbar.dto';
import { dataURItoBlob } from 'src/app/shared/helpers/image.helper';
import { sanitizeQuillText } from 'src/app/shared/helpers/utils';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { UploadParams, UploadService } from 'src/app/shared/services/upload/upload.service';
import { generateObjectId } from 'src/app/shared/utils/object-id.utils';
import { StorageService } from 'src/app/storage.service';
import { ModelsService } from 'src/app/views/my-space/my-models/models.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '../../../../app.service';
import { ContentStateService } from '../../content-state.service';
import { ContentService } from '../../content.service';
import { Chunk } from '../chunk.dto';
import { ChunkService, DeleteChunkData } from '../chunk.service';

import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { CreateChunkOptionsOverlayComponent } from './create-chunk-options-overlay/create-chunk-options-overlay.component';
import { CreateChunkService } from './create-chunk.service';
import { TableChunkEditorComponent } from './table-chunk-editor/table-chunk-editor.component';
const Embed = Quill.import('blots/embed') as any;

class FormulaBlot extends Embed {
  static blotName = 'formula';
  static tagName = 'SPAN';
  static className = 'ql-formula';

  static create(value: string) {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-value', value);
    node.textContent = value;
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('data-value') ?? '';
  }
}

Quill.register({ 'formats/formula': FormulaBlot }, true);

@Component({
  selector: 'app-create-chunk',
  templateUrl: './create-chunk.component.html',
  styleUrls: ['./create-chunk.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    QuillModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTabsModule,
    MatSelectModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    QuillToolbarComponent,
    TableChunkEditorComponent,
    InputComponent,
  ],
})
export class CreateChunkComponent implements OnInit, OnDestroy {
  public breakpointService = inject(BreakpointObserverService);
  private appService = inject(AppService);
  private toast = inject(ToastService);
  private aroute = inject(ActivatedRoute);
  private errorHandler = inject(ErrorHandlerService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  private modelService = inject(ModelsService);
  private contentService = inject(ContentService);
  private authService = inject(AuthService);
  private createChunkService = inject(CreateChunkService);
  private storageService = inject(StorageService);
  private envService = inject(EnvService);
  private contentStateService = inject(ContentStateService);
  private uploadService = inject(UploadService);
  private overlayService = inject(DynamicOverlayService);
  private dialog = inject(MatDialog);

  isEditorFocused = signal(false);
  private savedSelection: { index: number; length: number } | null = null;
  showLinkToolbar = signal(false);
  linkToolbarStyle: { [key: string]: string } = {};
  isEditingLink = signal(false);
  linkInputUrl = '';
  private activeLinkRange: { index: number; length: number } | null = null;
  activeLinkUrl = '';
  private insertBelowChunkSubscription: Subscription;
  private submitChunkSubscription: Subscription;
  private textTypeChangeSubscription: Subscription;
  private autosaveSubscription: Subscription;
  private contentChange$ = new Subject<string>();
  private isSettingContentProgrammatically = false;
  private initialContentBeforeDictation = '';
  dictationInsertedLength: number = 0;

  @Input() courseID;
  @Input() blogID;
  @Input() noteID;
  @Input() lecture;
  @Input() section;
  @Input() contentID;
  @Input() contentName;
  @Input() contentType;
  @Input() mode;
  @Input() chunkToEdit: Chunk;
  @Input() chunkType;
  @Input() showOptions = true;
  @Input() chunkAboveId;
  @Input() chunkBelowId;

  clickEvent = input<MouseEvent | null>(null);

  @Output() preCreatedChunk = new EventEmitter<any>();
  @Output() doneEditing = new EventEmitter<void>();
  modeToggled = new EventEmitter<any>();

  creatingChunk = toSignal(this.createChunkService.creatingChunk$);
  savingChunk = toSignal(this.createChunkService.savingChunk$);
  saveSuccess = toSignal(this.createChunkService.saveSuccess$);
  canUndo = signal(false);
  canRedo = signal(false);
  insertBelow = undefined;
  toolbarStyle: { [key: string]: string } = {};
  transcriberActive = false;
  showQuill = signal(true);
  showToolbar = signal(false);
  textHighlighted = signal(false);
  keyboardOffset = signal(0);
  private _viewportResizeListener: (() => void) | null = null;

  isHighlighted = computed(() => this.contentStateService.highlightedChunkId() === this.chunkToEdit?._id);

  public isPlaying = computed(() => {
    const playerState = this.contentStateService.playerState();
    return playerState?.chunkId === this.chunkToEdit?._id && playerState.status === 'playing';
  });

  public _editor: QuillEditorComponent;
  private quillWordRanges: { textNode: Node; startOffset: number; endOffset: number; word: string }[] = [];
  private isQuillTokenized = false;
  private lastActiveIndex = -1;
  private lastChunkId = '';
  private lastLanguage = '';
  private lastPlayingState = false;
  private lastTime = 0;
  private lastSelectionRange: { index: number; length: number } | null = null;
  private lastSavedContent = '';

  @ViewChild('mainQuillEditor')
  set mainEditor(editor: QuillEditorComponent) {
    if (editor) {
      this._editor = editor;
      // Initial check of history state
      this.updateHistoryState();
    }
  }

  @ViewChild('editorWrapper') editorWrapper: ElementRef;
  @ViewChild('linkInput') linkInput: InputComponent;

  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this._editor?.quillEditor && !this.elementRef.nativeElement.contains(event.target)) {
      if (this.isEditingLink()) {
        const selection = this.activeLinkRange || this.savedSelection;
        if (selection && selection.length > 0) {
          this._editor.quillEditor.formatText(selection.index, selection.length, 'background', false, 'silent');
        }
        this.isEditingLink.set(false);
      }
      this.showQuill.set(false);
      this.showToolbar.set(false);
      this.showLinkToolbar.set(false);
    } else {
      this.showQuill.set(true);
    }
  }

  @HostListener('keydown.control.enter', ['$event'])
  @HostListener('keydown.meta.enter', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    event.preventDefault();
    this.submitChunk();
  }

  activateEditor(): void {
    if (this.contentStateService.isLiveTranscribing() && this.contentStateService.highlightedChunkId() !== this.chunkToEdit?._id) return;

    if (!this.showQuill()) {
      this.showQuill.set(true);
    }
  }

  canCreate = true;
  currentLecture;
  chunk = {
    content: '',
    tags: [],
    courseID: undefined,
    blogID: undefined,
    lecture: undefined,
    section: undefined,
    noteID: undefined,
    contentID: undefined,
    contentName: undefined,
    isolateChunk: false,
    type: undefined,
    codeLanguage: undefined,
    contentType: undefined,
    language: undefined,
    generateVoice: undefined,
    selectedVoice: undefined,
    voiceGender: undefined,
    generateTranslation: undefined,
    generateEmbeddings: undefined,
  };
  generateTranslation = true;
  generateVoice = true;
  generateAiAfterTranscription = true;
  generateEmbeddings = true;
  chunkList = {
    content: [],
    tags: [],
  };
  separatorKeysCodes = [ENTER, COMMA];
  tags = [];
  contentToEmbed;
  contents = [];
  contentToSend = '';
  savedAIPrompt = '';
  submitMessage;
  listItem;
  loading = false;
  shouldSplitChunk = true;
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  AIContextTemplates = [];
  selectedAITemplateID = '';
  selectedTranscriptionTemplateID = '';
  codeLanguage = 'javascript';
  includeContext = false;
  codeLanguages = [
    { value: 'javascript', viewValue: 'Javascript' },
    { value: 'python', viewValue: 'Python' },
    { value: 'clike', viewValue: 'C++' },
    { value: 'rust', viewValue: 'Rust' },
    { value: 'css', viewValue: 'CSS' },
    { value: 'java', viewValue: 'Java' },
    { value: 'csharp', viewValue: 'C#' },
    { value: 'php', viewValue: 'PHP' },
    { value: 'go', viewValue: 'Go' },
    { value: 'ruby', viewValue: 'Ruby' },
    { value: 'html', viewValue: 'HTML' },
    { value: 'css', viewValue: 'CSS' },
    { value: 'vb', viewValue: 'Visual basic' },
  ];
  selectedLanguage = this.translate.currentLang || 'fr';

  selectedVoice = 'MALE';

  textContent = '';
  quillModules = {
    toolbar: '#custom-toolbar',
    clipboard: { matchVisual: false },
    history: { delay: 0, maxStack: 500, userOnly: true },
  };

  readOnlyQuillModules = {
    toolbar: false,
  };

  options: FormControl<string[]> = new FormControl<string[]>([]);
  optionList: string[] = ['generateTranslation', 'generateVoice', 'generateEmbeddings'];
  selectedExtractorTabIndex = 0;
  textGenerationSuscription: Subscription;
  AITextBeingGenerated: boolean;
  chunkCreatorConfig;
  socketSubscription: Subscription;
  interimTranscriptionData = '';
  transcriptionAITemplate = {};
  generativeAITemplate = {};
  generativeAISettings;
  transcriptionInterimData = '';
  liveTranscriptionStarted = false;
  liveTranscriptionStartedSub: Subscription;
  transcriptionSettings;

  userConfigurationSubscription: Subscription;
  generateTextEventSource: EventSource;
  selectedTextForTextGeneration: string;

  isLiveTranscriptionTarget = false;
  liveTranscriptionChunkIdSubscription: Subscription;

  constructor() {
    // TODO: to prevent default behavior when pressing enter after an image
    this.quillModules['keyboard'] = {
      bindings: {
        enterAfterImage: {
          key: 13, // Enter
          handler: () => {
            if (this._editor) {
              const quill = this._editor.quillEditor;
              const selection = quill.getSelection();
              if (selection && selection.length === 0) {
                const [leaf] = quill.getLeaf(selection.index - 1);
                if (leaf && leaf.statics.blotName === 'image') {
                  this.submitChunk();
                  return false; // Prevent default Quill handler
                }
              }
            }
            return true; // Let Quill handle it normally
          },
        },
      },
    };

    effect(() => {
      this.createChunkService.setEditorFocus(this.isEditorFocused());
    });

    effect(() => {
      const event = this.clickEvent();
      if (event) {
        // Workaround: Toggle showQuill to simulate clicking outside and re-entering
        this.showQuill.set(false);

        // Use a timeout to ensure the editor is visible and rendered before we try to focus it.
        if (!this.contentStateService.isLiveTranscribing()) {
          setTimeout(() => {
            this.showQuill.set(true);
            setTimeout(() => this.focusEditorAtClickPosition(event), 0);
          }, 0);
        }
      }
    });

    this.liveTranscriptionChunkIdSubscription = this.chunkService.liveTranscriptionChunkId$.subscribe((id) => {
      setTimeout(() => {
        if (id && ((this.chunkToEdit && this.chunkToEdit._id === id) || (!this.chunkToEdit && this.mode === 'create-chunk'))) {
          this.isLiveTranscriptionTarget = true;
          this.dictationInsertedLength = 0;
          let initial = this.contentToSend || '';
          if (initial.trim() === '<p><br></p>' || initial.trim() === '<p></p>') {
            initial = '';
          }
          this.initialContentBeforeDictation = initial;
          this.showQuill.set(true);
          this.focusEditor();
        } else {
          this.isLiveTranscriptionTarget = false;
        }
      }, 0);
    });

    this.chunkService.liveTranscriptionData$.pipe(takeUntilDestroyed()).subscribe(({ text, isFinal }) => {
      console.log('[CreateChunk] liveTranscriptionData received:', { isLiveTranscriptionTarget: this.isLiveTranscriptionTarget, text, isFinal });
      if (this.isLiveTranscriptionTarget) {
        if (this.contentStateService.liveTranscriptionSource() === 'dictation') {
          if (this._editor?.quillEditor) {
            const quill = this._editor.quillEditor;
            const docLength = quill.getLength();
            const Delta = (quill.constructor as any).import('delta');

            const dictationDelta = quill.clipboard.convert({ html: text });

            if (dictationDelta.ops.length > 0) {
              const lastOp = dictationDelta.ops[dictationDelta.ops.length - 1];
              if (typeof lastOp.insert === 'string' && lastOp.insert.endsWith('\n')) {
                lastOp.insert = lastOp.insert.slice(0, -1);
                if (lastOp.insert.length === 0) {
                  dictationDelta.ops.pop();
                }
              }
            }

            const insertIndex = Math.max(0, docLength - 1 - this.dictationInsertedLength);

            const charBeforeInsert = insertIndex > 0 ? quill.getText(insertIndex - 1, 1) : '\n';
            const needsSpace = charBeforeInsert !== '\n' && charBeforeInsert !== ' ';

            if (needsSpace) {
              if (dictationDelta.ops.length > 0 && typeof dictationDelta.ops[0].insert === 'string') {
                const firstStr = dictationDelta.ops[0].insert;
                if (!firstStr.startsWith(' ') && !firstStr.startsWith('\n')) {
                  dictationDelta.ops[0].insert = ' ' + firstStr;
                }
              }
            }

            const updateDelta = new Delta().retain(insertIndex).delete(this.dictationInsertedLength).concat(dictationDelta);

            quill.updateContents(updateDelta, 'silent');

            this.dictationInsertedLength = dictationDelta.length();
            quill.setSelection(quill.getLength(), 0, 'silent');

            this.contentToSend = quill.root.innerHTML;
            this.contentChange$.next(this.contentToSend);

            if (isFinal) {
              if (this.chunkToEdit?.type === 'canvas-transcript') {
                this.submitChunk();
              }
              this.dictationInsertedLength = 0;
            }
          }
        } else {
          let fullText = this.initialContentBeforeDictation || '';
          if (text) {
            if (fullText.trim().endsWith('</p>')) {
              const trimmed = fullText.trim();
              fullText = trimmed.substring(0, trimmed.length - 4) + ' ' + text + '</p>';
            } else {
              fullText += ' ' + text;
            }
          }

          this.contentToSend = fullText;
          if (this._editor?.quillEditor) {
            const quill = this._editor.quillEditor;
            const delta = quill.clipboard.convert({ html: fullText });
            quill.setContents(delta, 'silent');
            quill.setSelection(quill.getLength(), 0, 'silent');
          }

          this.contentChange$.next(this.contentToSend);

          if (isFinal) {
            if (this.chunkToEdit?.type === 'canvas-transcript') {
              this.submitChunk();
            }
          }
        }
      }
    });

    this.createChunkService.undo$.pipe(takeUntilDestroyed()).subscribe(() => this.undo());
    this.createChunkService.redo$.pipe(takeUntilDestroyed()).subscribe(() => this.redo());
    this.createChunkService.copy$.pipe(takeUntilDestroyed()).subscribe(() => this.copyContentToClipBoard());
    this.createChunkService.submitChunk$.pipe(takeUntilDestroyed()).subscribe(() => this.submitChunk());
    this.createChunkService.textGenerated$.pipe(takeUntilDestroyed()).subscribe((text) => (this.contentToSend = text));
    this.createChunkService.uploadComplete$.pipe(takeUntilDestroyed()).subscribe(() => this.onUploadComplete());
    this.createChunkService.recordingSaved$.pipe(takeUntilDestroyed()).subscribe(({ file, type }) => this.onRecordingSaved(file, type));

    this.contentStateService.addTextToEditorCommand$.pipe(takeUntilDestroyed()).subscribe((text: string) => {
      this.isSettingContentProgrammatically = true;
      this.contentToSend = this.contentToSend ? `${this.contentToSend}<p>${text}</p>` : text;
      this.focusEditor();
      setTimeout(() => (this.isSettingContentProgrammatically = false), 0);
    });

    this.contentStateService.createChunkWithTextCommand$.pipe(takeUntilDestroyed()).subscribe((text: string) => {
      this.isSettingContentProgrammatically = true;
      this.contentToSend = this.contentToSend ? `${this.contentToSend}<p>${text}</p>` : text;
      this.submitChunk();
      setTimeout(() => (this.isSettingContentProgrammatically = false), 0);
    });

    toObservable(this.contentStateService.highlightedChunkId)
      .pipe(takeUntilDestroyed())
      .subscribe((id) => {
        if (this.chunkToEdit?._id === id && this.contentStateService.isLiveTranscribing()) {
          this.activateEditor();
        }
      });

    this.userConfigurationSubscription = this.authService.userConfiguration$.subscribe(async (res) => {
      if (res.transcriptionSettings) {
        this.transcriptionSettings = res.transcriptionSettings;
      }
      if (res.generativeAISettings) {
        this.selectedAITemplateID = res.generativeAISettings.template;
        this.generativeAISettings = res.generativeAISettings;
        if (this.generativeAISettings.template && this.generativeAISettings.template != 'none') {
        } else {
          this.selectedAITemplateID = 'none';
          this.generativeAITemplate = { _id: 'none', name: 'Global.None' };
        }
      }
      if (res.transcriptionSettings) {
        this.transcriptionSettings = res.transcriptionSettings;
        if (this.transcriptionSettings.template && this.transcriptionSettings.template != 'none') {
        } else {
          this.transcriptionAITemplate = {};
        }
      }
      if (res.chunkCreator) {
        this.chunkCreatorConfig = res.chunkCreator;
        const options = [];
        for (const element in res.chunkCreator) {
          if (res.chunkCreator[element]) {
            options.push(element);
          }
        }
        this.options.setValue(options);
        // NOTE: For now we let the user configure the chunk creator config
        this.generateTranslation = false; //this.chunkCreatorConfig.generateTranslation;
        this.generateVoice = false; //this.chunkCreatorConfig.generateVoice;
        this.generateAiAfterTranscription = true; //this.chunkCreatorConfig.generateAiAfterTranscription;
        this.generateEmbeddings = false; //this.chunkCreatorConfig.generateEmbeddings;
      }
    });
    this.liveTranscriptionStartedSub = this.chunkService.liveTranscriptionStarted$.subscribe((value: boolean) => {
      this.liveTranscriptionStarted = value;
      if (value == false) {
        this.transcriptionInterimData = '';
        this.contentToSend;
      }
    });

    this.insertBelowChunkSubscription = this.contentService.insertBelowChunk$.subscribe((id) => {
      this.insertBelow = id;
    });
    this.translate.onLangChange.subscribe((res) => {
      this.selectedLanguage = this.translate.currentLang;
      this.isQuillTokenized = false;
      this.clearQuillHighlighting();
    });

    // ++ Subscribe to the submit event from the service
    this.submitChunkSubscription = this.createChunkService.submitChunk$.subscribe(() => {
      // this.submitChunk();
    });

    // // ++ Subscribe to the text type change event from the service
    this.textTypeChangeSubscription = this.createChunkService.textTypeChange$.subscribe((type: any) => {
      this.setEditorType(type);
    });

    effect(() => {
      const playing = this.isPlaying();
      const currentTime = this.contentStateService.playerCurrentTime();
      const chunk = this.chunkToEdit;
      const currentLang = this.translate.currentLang;

      if (chunk?._id !== this.lastChunkId || currentLang !== this.lastLanguage) {
        this.clearQuillHighlighting();
        this.lastChunkId = chunk?._id || '';
        this.lastLanguage = currentLang || '';
        this.isQuillTokenized = false;
      }

      if (playing && chunk) {
        this.highlightQuillWordAtTime(currentTime);
      } else {
        this.clearQuillHighlighting();
      }
    });
  }

  // TODO: [RTA-1917] devrait normalement transformer les backtick en code mais ne fonctionne pas pour le moment
  // à revoir.
  onEditorCreated(quill: any) {
    const editorElement = quill.root;
    if (editorElement) {
      editorElement.addEventListener('contextmenu', (event: MouseEvent) => {
        const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0));
        if (isTouch) {
          event.preventDefault();
        }
      });
    }

    quill.on('text-change', (delta: any, oldDelta: any, source: string) => {
      this.isQuillTokenized = false;
      if (source !== 'user') return;
      const ops = delta.ops;
      if (!ops || ops.length === 0) return;

      const lastOp = ops[ops.length - 1];
      if (lastOp && lastOp.insert === '`') {
        const selection = quill.getSelection();
        if (!selection) return;

        const index = selection.index;
        const text = quill.getText(0, index - 1);
        const lastLineBreak = text.lastIndexOf('\n');
        const searchStart = lastLineBreak !== -1 ? lastLineBreak + 1 : 0;
        const lineText = text.substring(searchStart);

        const lastBacktickIndexInLine = lineText.lastIndexOf('`');
        if (lastBacktickIndexInLine !== -1) {
          const startPos = searchStart + lastBacktickIndexInLine;
          const codeText = text.substring(startPos + 1);

          if (codeText.length > 0) {
            quill.deleteText(index - 1, 1);
            quill.deleteText(startPos, 1);

            quill.formatText(startPos, codeText.length, 'code', true);
            quill.setSelection(startPos + codeText.length);
            quill.format('code', false);
          }
        }
      }
    });
  }

  private tokenizeQuillContent() {
    if (this.isQuillTokenized) return;
    const quill = this._editor?.quillEditor;
    if (!quill) return;
    const root = quill.root;
    if (!root) return;

    this.quillWordRanges = [];
    const traverse = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentNode;
        if (!parent) return;

        const parentTagName = (parent as HTMLElement).tagName?.toLowerCase();
        if (['script', 'style', 'code', 'noscript'].includes(parentTagName)) {
          return;
        }
        if ((parent as HTMLElement).closest('.ql-formula') || (parent as HTMLElement).closest('.katex')) {
          return;
        }

        const text = node.nodeValue || '';
        if (!text.trim()) return;

        const regex = /([a-zA-Z0-9À-ÿ\u00C0-\u017F'-]+)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
          const matchIndex = match.index;
          const matchedWord = match[0];
          this.quillWordRanges.push({
            textNode: node,
            startOffset: matchIndex,
            endOffset: matchIndex + matchedWord.length,
            word: matchedWord,
          });
        }
      } else {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          traverse(child);
        }
      }
    };

    traverse(root);
    this.isQuillTokenized = true;
  }

  private highlightQuillWordAtTime(currentTime: number) {
    const currentLang = this.translate.currentLang;
    const currentAudio = this.chunkToEdit?.audios?.find(
      (a: any) => a._id === currentLang || a.id === currentLang || a.filename?.includes(`_${currentLang}.`) || a.fileLink?.includes(`_${currentLang}.`)
    );
    const words = currentAudio?.words || [];

    if (!words || words.length === 0) {
      this.clearQuillHighlighting();
      return;
    }

    let activeIndex = -1;
    const padding = 0.1; // 100ms padding to account for lag
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i].startTime <= currentTime + padding) {
        activeIndex = i;
        break;
      }
    }

    this.tokenizeQuillContent();

    if (this.quillWordRanges.length === 0) {
      this.clearQuillHighlighting();
      return;
    }

    if (activeIndex >= this.quillWordRanges.length) {
      activeIndex = this.quillWordRanges.length - 1;
    }

    if (activeIndex === -1) {
      this.clearQuillHighlighting();
      return;
    }

    const wordRange = this.quillWordRanges[activeIndex];
    try {
      const range = new Range();
      range.setStart(wordRange.textNode, wordRange.startOffset);
      range.setEnd(wordRange.textNode, wordRange.endOffset);

      const win = window as any;
      if (typeof win.CSS !== 'undefined' && win.CSS.highlights) {
        if (win.Highlight) {
          const highlight = new win.Highlight(range);
          win.CSS.highlights.set('quill-word-highlight', highlight);
        }
      }
    } catch (e) {
      console.error('Failed to highlight word in Quill editor:', e);
    }
  }

  private clearQuillHighlighting() {
    const win = window as any;
    if (typeof win.CSS !== 'undefined' && win.CSS.highlights) {
      win.CSS.highlights.delete('quill-word-highlight');
    }
  }

  setEditorType(type: any): void {
    this.chunkType = type;
    this.chunkParams(false);
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.visualViewport) {
      this._viewportResizeListener = () => this.updateVisualViewportOffset();
      window.visualViewport.addEventListener('resize', this._viewportResizeListener);
      window.visualViewport.addEventListener('scroll', this._viewportResizeListener);
      this.updateVisualViewportOffset();
    }

    const Size = Quill.import('attributors/style/size') as any;
    Size.whitelist = ['12px', '14px', '16px', '18px', '20px', '22px', false];
    Quill.register(Size, true);

    const Font = Quill.import('attributors/style/font') as any;
    const fonts = ['arial', 'courier-new', 'georgia', 'helvetica', 'lucida-sans', 'tahoma', 'times-new-roman', 'trebuchet-ms', 'verdana'];
    Font.whitelist = fonts;
    Quill.register(Font, true);

    const Color = Quill.import('attributors/style/color') as any;
    const Background = Quill.import('attributors/style/background') as any;
    Color.whitelist = COLOR_WHITELIST;
    Background.whitelist = COLOR_WHITELIST;
    Quill.register(Color, true);
    Quill.register(Background, true);

    if (this.chunkToEdit) {
      // Focus the editor instance for the chunk being edited
      this.focusEditor();
      this.chunkService.setChunkToFocusOn(null);
    }

    // ++ Set create mode based on the component's mode
    if (this.mode === 'create-chunk' || this.mode === 'edit-chunk' || this.mode === 'modify-chunk') {
      this.createChunkService.setCreateMode(true);
    }

    this.chunkParams(false);

    if (this.mode != 'modify-chunk') {
      this.contentToSend = '';
    }
    this.storageService.find('chunkCreator', [this.noteID ? this.noteID : this.lecture ? this.lecture._id : this.blogID]).then((res: any) => {
      if (res.length) {
        this.contentToSend = res[0].content || '';
      }
    });
    if (this.mode == 'modify-chunk') {
      const currentEdition = this.chunkToEdit.editions.find((edition) => edition.language == this.selectedLanguage);
      this.contentToSend = currentEdition ? currentEdition.content : '';
      this.lastSavedContent = this.contentToSend;
      this.setupAutosave();
    }
  }

  async ngOnDestroy(): Promise<void> {
    if (typeof window !== 'undefined' && window.visualViewport && this._viewportResizeListener) {
      window.visualViewport.removeEventListener('resize', this._viewportResizeListener);
      window.visualViewport.removeEventListener('scroll', this._viewportResizeListener);
    }

    this.clearQuillHighlighting();
    this.liveTranscriptionChunkIdSubscription?.unsubscribe();
    this.liveTranscriptionStartedSub?.unsubscribe();
    this.userConfigurationSubscription?.unsubscribe();
    this.insertBelowChunkSubscription?.unsubscribe();
    this.submitChunkSubscription?.unsubscribe();
    this.textTypeChangeSubscription?.unsubscribe();

    this.createChunkService.setCreateMode(false);
    await this.modifyChunk();
    this.autosaveSubscription?.unsubscribe();
  }

  onRecordingSaved(file: Blob, type: 'video' | 'image') {
    const params: UploadParams = {
      contentID: this.contentStateService.contentId,
      blogID: this.contentStateService.blogId,
      courseID: this.contentStateService.courseId,
      contentName: this.contentName,
      insertBelowID: this.chunkAboveId,
      insertAsFirst: !this.chunkAboveId ? true : false,
    };

    const insertId = this.chunkAboveId ?? null;
    const aFile = file instanceof File ? file : new File([file], `${uuidv4()}.${type === 'image' ? 'png' : 'mp4'}`, { type: file.type });
    const fileName = aFile.name;

    this.contentStateService.setUploadState({ status: 'uploading', progress: 0, insertId, fileName });

    this.uploadService.uploadFileWithProgress(aFile, params).subscribe({
      next: (progress) => {
        if (progress < 100) {
          this.contentStateService.setUploadState({ status: 'uploading', progress, insertId, fileName });
        } else {
          this.contentStateService.setUploadState({ status: 'creating', progress: 100, insertId, fileName });
        }
      },
      error: (err) => {
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
        this.contentStateService.setUploadState(null);
      },
      complete: () => {
        this.chunkService.passChunkEditEvent('all');
        this.contentStateService.setUploadState(null);
      },
    });
  }

  private setupAutosave(): void {
    this.autosaveSubscription = this.contentChange$
      .pipe(
        debounceTime(2500),
        distinctUntilChanged(),
        filter(() => this.contentToSend !== this.lastSavedContent)
      )
      .subscribe(() => this.modifyChunk());
  }

  getTemplateData(id: string) {
    return new Promise((resolve, reject) => {
      this.modelService.getTemplate(id).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  private add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      const name = value.trim();
      this.tags.push({ name });
      this.contentService.getContentByName(name).subscribe((resp) => {
        const index = this.contents.findIndex((e) => e.name === resp.content.name);
        if (index) {
          this.contents.splice(index, 1);
          this.contents.splice(index, 0, resp.content);
        } else {
          this.contents.push(resp.content);
        }
      });
    }

    if (input) {
      input.value = '';
    }
  }

  private remove(fruit: any): void {
    const index = this.tags.indexOf(fruit);

    if (index >= 0) {
      const removedTag = this.tags.splice(index, 1);
      const i = this.contents.findIndex((e) => e.name == removedTag[0].name);
      if (~i) {
        this.contents.splice(i, 1);
      }
    }
  }

  private splitChunk(chunk) {
    let chunks = [];
    let split = [];
    if (this.insertBelow) {
      chunks = [chunk];
    } else {
      const contentToSend = chunk.content;
      const l1split = contentToSend.split('</p>');
      for (const l2split of l1split) {
        const l3split = l2split.split('<br>');
        for (let l4split of l3split) {
          if (l4split == '') {
            continue;
          }
          l4split += '</p>';
          split.push(l4split);
        }
      }
      for (const element of split) {
        const currChunk = JSON.parse(JSON.stringify(chunk));
        currChunk.content = element;
        chunks.push(currChunk);
      }
    }
    return chunks;
  }

  private async createChunk(isolateChunk): Promise<any[]> {
    this.chunkParams(isolateChunk);
    this.chunk.courseID = this.aroute.snapshot.paramMap.get('course');
    this.chunk.tags = this.tags;
    this.chunk.content = this.contentToSend;

    let parts = [];
    if (this.shouldSplitChunk) {
      parts = this.splitChunk(this.chunk);
    } else {
      parts = [this.chunk];
    }

    const processedParts = [];
    const imageUploads = [];

    for (const part of parts) {
      const content = part.content;
      const imgRegex = /<img src="(data:image\/[^;]+;base64,[^\"]+)">/g;
      let match;
      let lastIndex = 0;

      const tempContentParts = [];

      while ((match = imgRegex.exec(content)) !== null) {
        // Add text before the image
        if (match.index > lastIndex) {
          tempContentParts.push({
            type: this.chunk.type || 'html',
            content: content.substring(lastIndex, match.index),
          });
        }

        // Handle the image
        const dataUri = match[1];
        const tempId = uuidv4();
        const blob = dataURItoBlob(dataUri);

        await this.storageService.db.images.put({ _id: tempId, blob: blob, _ts: moment().unix() });

        tempContentParts.push({
          type: 'image',
          content: tempId,
        });

        imageUploads.push({ tempId, blob });

        lastIndex = imgRegex.lastIndex;
      }

      // Add remaining text
      if (lastIndex < content.length) {
        tempContentParts.push({
          type: this.chunk.type || 'html',
          content: content.substring(lastIndex),
        });
      }

      for (const p of tempContentParts) {
        processedParts.push({
          ...part,
          content: p.content,
          type: p.type,
          alternateID: p.type === 'image' ? p.content : undefined,
        });
      }
    }

    parts = processedParts.length > 0 ? processedParts : parts;

    parts = parts.map((p) => ({
      ...p,
      type: p.type === 'editor' ? 'html' : p.type,
      status: 'creating',
      content: p.type === 'html' ? sanitizeQuillText(p.content) : p.content,
    }));

    this.canCreate = false;
    let optimisticChunks = [];

    try {
      this.createChunkService.setCreatingChunk(true);
      // 1. Optimistic Creation
      optimisticChunks = await this.chunkService.createChunksInIndex(parts);
      optimisticChunks.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

      // 2. Update status to 'saving'
      const chunksToSave = optimisticChunks.map((c) => ({ ...c, status: 'saving' }));
      await this.storageService.bulkUpdate('chunks', chunksToSave);
      chunksToSave.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

      // 3. API Call
      const userData = JSON.parse(localStorage.getItem('userData'));
      const apiPayload = chunksToSave.map((chunk) => ({
        ...chunk.chunkParams,
        alternateID: chunk._id,
        _id: chunk._id,
        user: {
          id: userData.id,
          fullName: `${userData.firstName} ${userData.lastName}`,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
        },
      }));

      const serverResponse = await firstValueFrom(
        this.chunkService.createChunk({
          chunks: apiPayload,
          insertBelowID: this.insertBelow,
        })
      );

      // 4. Reconciliation on Success
      this.canCreate = true;
      this.contentToSend = '';

      const elementID = this.noteID ? this.noteID : this.lecture ? this.lecture._id : this.blogID;
      if (elementID) {
        this.storageService.remove('chunkCreator', [elementID]);
      }

      const tempIds = optimisticChunks.map((c) => c._id);
      const finalChunks = serverResponse.map((res) => ({ ...res.chunk, status: 'saved' }));

      if (serverResponse.length > 0) {
        const updatedContent = serverResponse[serverResponse.length - 1].content;
        await this.storageService.store('contents', updatedContent);
      }

      await this.storageService.bulkRemove('chunks', tempIds);
      await this.storageService.bulkStore('chunks', finalChunks);

      finalChunks.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));
      this.chunkParams(false);

      if (this.insertBelow) {
        this.contentService.setInsertBelowChunk(null);
      }

      // Handle image uploads and updates
      for (const chunk of finalChunks) {
        if (chunk.type === 'image') {
          const mainEdition = chunk.editions.find((e) => e._id === chunk.main);
          if (mainEdition) {
            const tempId = mainEdition.content;
            const uploadData = imageUploads.find((u) => u.tempId === tempId);
            if (uploadData) {
              this.uploadBlob(uploadData.blob).then((fileUrl) => {
                this.chunkService
                  .editChunk({
                    id: chunk._id,
                    content: fileUrl,
                    language: mainEdition.language,
                    type: 'image',
                    contentID: this.contentID,
                    edition: chunk.editions.find((e) => e.language === this.translate.currentLang) || mainEdition,
                  })
                  .subscribe(() => {
                    this.storageService.db.images.delete(tempId);
                  });
              });
            }
          }
        }
      }
      return finalChunks;
    } catch (error) {
      // 5. Handle Errors
      this.canCreate = true;
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });

      if (optimisticChunks.length > 0) {
        const chunksToUpdate = optimisticChunks.map((c) => ({ ...c, status: 'error' }));
        await this.storageService.bulkUpdate('chunks', chunksToUpdate);
        chunksToUpdate.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));
      }
      this.errorHandler.handle401Error(error);
      return [];
    } finally {
      this.createChunkService.setCreatingChunk(false);
    }
  }

  private focusEditor(): void {
    // Use a timeout to ensure the editor is visible and rendered.
    setTimeout(() => {
      if (this._editor?.quillEditor) {
        this._editor.quillEditor.focus();
        const length = this._editor.quillEditor.getLength();
        this._editor.quillEditor.setSelection(length, 0, 'user');
      }
    }, 0);
  }

  private async uploadBlob(blob: Blob): Promise<string> {
    const filename = `${uuidv4()}.png`;
    const file = new File([blob], filename, { type: 'image/png' });

    return new Promise((resolve, reject) => {
      this.uploadService.uploadFileAndGetProgress(file, 'image/' + filename).on(
        'state_changed',
        (snapshot) => {
          // You can handle progress here if needed
        },
        (error) => {
          console.error('Upload failed', error);
          reject(error);
        },
        () => {
          const fileLink = `${environment.linkPrefix}${this.envService.appName}/image/${filename}`;
          resolve(fileLink);
        }
      );
    });
  }

  private pendingSaveRequest = false;
  private pendingSavePromise: Promise<void> | null = null;
  private resolvePendingSave: (() => void) | null = null;

  private checkPendingSave(): void {
    if (this.pendingSaveRequest) {
      this.pendingSaveRequest = false;
      const resolve = this.resolvePendingSave;
      this.pendingSavePromise = null;
      this.resolvePendingSave = null;

      this.modifyChunk().then(() => {
        if (resolve) resolve();
      });
    }
  }

  private async modifyChunk(imagePasted?: boolean): Promise<void> {
    if (this.savingChunk()) {
      if (!imagePasted) {
        this.pendingSaveRequest = true;
        if (!this.pendingSavePromise) {
          this.pendingSavePromise = new Promise((resolve) => {
            this.resolvePendingSave = resolve;
          });
        }
        return this.pendingSavePromise;
      }
      return;
    }

    if (this.contentToSend === this.lastSavedContent && !imagePasted) {
      return;
    }

    const snapshotContent = this.contentToSend;

    const singleImageRegex = /^\s*<p>\s*<img src="(data:image\/[^;]+;base64,[^\"]+)">\s*<\/p>\s*$/;
    const isSingleImage = singleImageRegex.exec(snapshotContent.trim());

    if (isSingleImage) {
      this.uploadPastedImage(isSingleImage[1], snapshotContent);
      return;
    }

    if (snapshotContent.trim().length < 1) {
      return;
    }

    this.createChunkService.setSavingChunk(true);
    this.createChunkService.setSaveSuccess(false);

    const parts = this.getChunkContentParts(snapshotContent);
    const isSplitting = parts.length > 1;

    try {
      if (isSplitting && this._editor?.quillEditor) {
        this._editor.quillEditor.disable();
      }

      if (!isSplitting) {
        await this.saveSingleChunk(snapshotContent);
      } else {
        await this.splitAndSaveMultipleChunks(parts);
      }

      this.createChunkService.setSaveSuccess(true);

      setTimeout(() => {
        this.createChunkService.setSaveSuccess(false);
      }, 2000);
    } catch (error) {
      this.errorHandler.handle401Error(error);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
    } finally {
      if (isSplitting && this._editor?.quillEditor) {
        this._editor.quillEditor.enable();
        this.focusEditor();
      }
      this.createChunkService.setSavingChunk(false);
      this.checkPendingSave();
    }
  }

  private getChunkContentParts(snapshotContent: string): string[] {
    if (this.chunkToEdit.type === 'html') {
      return snapshotContent
        .split('</p>')
        .map((p) => p.trim())
        .filter((p) => p)
        .map((p) => p + '</p>');
    }
    return [snapshotContent];
  }

  private async saveSingleChunk(snapshotContent: string): Promise<void> {
    const type = this.chunkToEdit.type;
    const blankEdition = this.chunkToEdit.editions[0];
    blankEdition.language = this.selectedLanguage;
    blankEdition.content = sanitizeQuillText(snapshotContent);
    const data: any = {
      id: this.chunkToEdit._id,
      edition: this.chunkToEdit.editions.find((edition) => edition.language == this.selectedLanguage) || blankEdition,
      type: type,
      language: this.selectedLanguage,
      content: sanitizeQuillText(snapshotContent),
      contentID: this.contentID,
    };

    if (this.chunkToEdit.status === 'processing') {
      data.status = 'ready';
    }

    await firstValueFrom(this.chunkService.editChunk(data));
    this.lastSavedContent = snapshotContent;
  }

  private async splitAndSaveMultipleChunks(parts: string[]): Promise<void> {
    const firstPart = parts.shift() || '';

    // --- 1. LOCAL OPTIMISTIC UPDATES ---

    // A. Edit the original chunk locally and update local storage so it does not get reloaded with old content
    const editionToUpdate = this.chunkToEdit.editions.find((edition) => edition.language == this.selectedLanguage) || this.chunkToEdit.editions[0];
    editionToUpdate.content = sanitizeQuillText(firstPart);
    await this.storageService.store('chunks', { ...this.chunkToEdit });
    this.contentStateService.updateChunkLocally(this.chunkToEdit._id, { ...this.chunkToEdit });

    // B. Create the new empty/split chunks locally
    this.chunkParams(false);
    const newChunks = parts.map((partContent) => ({
      ...this.chunk,
      _id: generateObjectId(),
      content: sanitizeQuillText(partContent),
    }));

    let optimisticChunks: any[] = [];
    if (newChunks.length > 0) {
      optimisticChunks = await this.chunkService.createChunksInIndex(newChunks, this.chunkToEdit._id);
      optimisticChunks.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

      // Update the UI state instantly
      this.contentStateService.insertChunksLocally(optimisticChunks, this.chunkToEdit._id);

      // Focus the newly created chunk
      this.chunkService.setChunkToFocusOn(optimisticChunks[0]._id);
    }

    this.contentToSend = firstPart;
    this.lastSavedContent = firstPart;
    if (this._editor) {
      this._editor.quillEditor.history.clear();
      this.updateHistoryState();
    }

    // --- TESTING ---
    // return;

    // --- 2. BACKGROUND API CALLS ---
    if (optimisticChunks.length > 0) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const apiPayload = optimisticChunks.map((chunk) => ({
        ...chunk.chunkParams,
        alternateID: chunk._id,
        _id: chunk._id,
        user: {
          id: userData.id,
          fullName: `${userData.firstName} ${userData.lastName}`,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
        },
      }));

      this.chunkService
        .createChunk({
          chunks: apiPayload,
          insertBelowID: this.chunkToEdit._id,
        })
        .subscribe();
    }

    const data: any = {
      id: this.chunkToEdit._id,
      edition: editionToUpdate,
      type: this.chunkToEdit.type,
      language: this.selectedLanguage,
      content: sanitizeQuillText(firstPart),
      contentID: this.contentID,
    };
    if (this.chunkToEdit.status === 'processing') {
      data.status = 'ready';
    }
    this.chunkService.editChunk(data).subscribe();
  }

  private uploadPastedImage(dataUri: string, snapshotContent: string): void {
    // Replicate the logic from onRecordingSaved as requested
    this.createChunkService.setSavingChunk(true);

    const blob = dataURItoBlob(dataUri);
    const file = new File([blob], `${uuidv4()}.png`, { type: 'image/png' });

    const params: UploadParams = {
      contentID: this.contentStateService.contentId,
      blogID: this.contentStateService.blogId,
      courseID: this.contentStateService.courseId,
      contentName: this.contentName,
      insertBelowID: this.chunkAboveId,
      insertAsFirst: !this.chunkAboveId ? true : false,
    };

    const insertId = this.chunkAboveId ?? null;
    const fileName = file.name;
    this.contentStateService.setUploadState({ status: 'uploading', progress: 0, insertId, fileName });

    this.uploadService.uploadFileWithProgress(file, params).subscribe({
      next: (progress) => {
        if (progress < 100) {
          this.contentStateService.setUploadState({ status: 'uploading', progress, insertId, fileName });
        } else {
          this.contentStateService.setUploadState({ status: 'creating', progress: 100, insertId, fileName });
        }
      },
      error: (err) => {
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
        this.contentStateService.setUploadState(null);
        this.createChunkService.setSavingChunk(false);
        this.checkPendingSave();
      },
      complete: () => {
        this.chunkService.passChunkEditEvent('all');
        if (this.contentToSend === snapshotContent) {
          this.contentToSend = '';
        }
        this.doneEditing.emit();
        this.createChunkService.setSavingChunk(false);
        this.contentStateService.setUploadState(null);
        this.checkPendingSave();
      },
    });
  }

  private async handleNewLineAtEnd(): Promise<void> {
    // 1. Save the current chunk.
    await this.modifyChunk();

    // 2. Prepare to create a new empty chunk below the current one.
    this.contentToSend = '';
    this.insertBelow = this.chunkToEdit._id;

    // 3. Create the new chunk.
    await this.createChunk(false);

    // 4. Signal that editing is done for the current chunk component.
    this.doneEditing.emit();
  }

  private deleteChunk(): void {
    if (!this.chunkToEdit?._id || !this.contentID || !this.chunkToEdit.type) {
      return;
    }

    const data: DeleteChunkData = {
      chunkID: this.chunkToEdit._id,
      contentID: this.contentID,
      type: this.chunkToEdit.type,
    };

    this.chunkService.deleteChunk(data).subscribe({
      next: () => {
        this.doneEditing.emit();
        this.chunkService.setChunkToFocusOn(this.chunkAboveId);
      },
      error: (err) => {
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
      },
    });
  }

  private createChunkEdition(): void {
    if (this.contentToSend.trim().length < 1) {
      this.toast.snackbar.open(this.translate.instant('Snackbar.ChunkCreatedSuccess'), 'Close', {
        duration: 2000,
      });
      return;
    }

    const type = this.chunkType || 'html';

    const data: any = {
      id: this.chunkToEdit._id,
      edition: this.contentToSend,
      type: type,
    };

    data.language = this.selectedLanguage;

    this.chunkService.generateChunkEdition(data).subscribe();
  }

  private chunkParams(isolateChunk?): void {
    this.chunk.generateEmbeddings = this.generateEmbeddings;
    this.chunk.noteID = this.noteID;
    this.chunk.courseID = this.courseID;
    this.chunk.content = this.contentToSend;
    this.chunk.blogID = this.blogID;
    this.chunk.lecture = this.lecture;
    this.chunk.section = this.section;
    this.chunk.contentID = this.contentID;
    this.chunk.contentName = this.contentName;
    this.chunk.isolateChunk = isolateChunk;
    this.chunk.generateTranslation = this.generateTranslation;
    this.chunk.type = this.chunkType || 'html';
    this.chunk.language = this.selectedLanguage;
    this.chunk.contentType = this.contentType;
    this.chunk.generateVoice = this.generateVoice;
    this.chunk.voiceGender = this.selectedVoice;
    this.createChunkService.updateChunk(this.chunk);
  }

  onUploadComplete() {
    this.chunkService.passChunkEditEvent('all');
  }

  changeSelection(val, event?) {
    if (event?.source._id == 'editor-type') {
      this.createChunkService.changeTextType(val);
    }
  }

  submitChunk() {
    if (this.mode == 'create-chunk') {
      this.createChunk(false);
    } else if (this.mode == 'edit-chunk') {
      this.createChunkEdition();
    } else {
      this.modifyChunk();
    }
  }

  async onTypedText(event?: { delta: any; source: string }) {
    if (this.isSettingContentProgrammatically) {
      return; // Bypass handler if content is being set by our service
    }
    const isImagePaste = event?.source === 'user' && event.delta.ops.some((op) => op.insert?.image);

    if (isImagePaste) {
      // An image was pasted. Check if the editor was empty before this paste.
      // getText() on an editor with just an image returns '\n', so trimming it makes it empty.
      if (this._editor.quillEditor.getText().trim() === '') {
        // PASTE IN EMPTY EDITOR: This is the desired case.
        // Disable the editor and proceed with creating the image chunk.
        this._editor.quillEditor.disable();
        setTimeout(async () => {
          try {
            await this.modifyChunk();
          } finally {
            if (this.elementRef.nativeElement.isConnected) {
              this._editor.quillEditor.enable();
            }
          }
        }, 100);
        return; // Stop further processing
      } else {
        // PASTE IN NON-EMPTY EDITOR: This is the case to prevent.
        // Undo the paste immediately.
        this._editor.quillEditor.history.undo();
        return; // Stop further processing
      }
    }

    // <p></p> is a what quill send on a line break for an empty paragraph
    // we transform it into a single empty string  then request the creation of the empty chunk
    if (this.contentToSend === '<p></p><p></p>') {
      this.contentToSend = '';
    }
    if ((this.mode === 'modify-chunk' || this.mode === 'create-chunk') && event && event.source === 'user') {
      if (event.delta.ops.some((op) => typeof op.insert === 'string' && op.insert.includes('\n'))) {
        this.submitChunk();
      } else if (this.mode === 'modify-chunk') {
        this.contentChange$.next(this.contentToSend);
      }
    }

    const elementID = this.noteID ? this.noteID : this.lecture ? this.lecture._id : this.blogID;
    if (!elementID) {
      return;
    }
    this.storageService.store('chunkCreator', {
      _id: elementID,
      content: this.contentToSend,
    });
  }

  private getContentToInclude() {
    if (this.includeContext) {
      if (this.contentToEmbed) {
        return this.contentToEmbed.contentID;
      } else {
        return this.contentID;
      }
    }
    return null;
  }

  updateVisualViewportOffset() {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const viewport = window.visualViewport;
    const offset = Math.max(0, window.innerHeight - (viewport.offsetTop + viewport.height));
    this.keyboardOffset.set(offset);
  }

  onSelectionChanged(event: any) {
    const { range, source } = event;
    if (source === 'user') {
      this.isEditorFocused.set(true);
    }

    if (this.isEditingLink()) {
      if (range) {
        const selection = this.activeLinkRange || this.savedSelection;
        if (!selection || range.index !== selection.index || range.length !== selection.length) {
          const quill = this._editor.quillEditor;
          quill.formatText(selection.index, selection.length, 'background', false, 'silent');
          this.isEditingLink.set(false);
        } else {
          return;
        }
      } else {
        return;
      }
    }

    this.showLinkToolbar.set(false);

    if (range && range.length > 0) {
      this.savedSelection = { index: range.index, length: range.length };
      this.textHighlighted.set(true);
      this.showToolbar.set(true);
      if (this.breakpointService.isSmallScreen() || this.breakpointService.isTabletScreen()) {
        this.toolbarStyle = {
          position: 'fixed',
          bottom: '0',
          left: '0',
          right: '0',
          width: '100%',
          transform: 'none',
          padding: '8px',
          'background-color': 'white',
          'box-shadow': '0 -2px 10px rgba(0, 0, 0, 0.15)',
          'border-top': '1px solid #e0e0e0',
          'border-radius': '0',
          'z-index': '9999',
        };
      } else {
        const editor = this._editor.quillEditor;
        const selectionBounds = editor.getBounds(range.index, range.length);

        const editorWidth = editor.container.offsetWidth;
        const toolbarEstimatedWidth = 796;
        const toolbarHalfWidth = toolbarEstimatedWidth / 2;

        const idealLeft = selectionBounds.left + selectionBounds.width / 2;

        const clampedLeft = Math.max(toolbarHalfWidth, Math.min(editorWidth - toolbarHalfWidth, idealLeft));

        const top = selectionBounds.top - 50;

        this.toolbarStyle = {
          position: 'absolute',
          top: `${top}px`,
          left: `${clampedLeft}px`,
          transform: 'translateX(-50%)',
          width: 'max-content',
          'max-width': '60vw',
          padding: '0',
          'background-color': 'white',
          'box-shadow': '0 2px 10px rgba(0, 0, 0, 0.1)',
          'border-radius': '12px',
          border: '1px solid #e0e0e0',
          'z-index': '1000',
        };
      }
    } else if (range && range.length === 0) {
      this.showToolbar.set(false);
      this.textHighlighted.set(false);

      const quill = this._editor.quillEditor;
      const format = quill.getFormat(range.index, 1);
      if (typeof format.link === 'string') {
        const linkFormat = format.link as string;
        const leaf = quill.getLeaf(range.index);
        if (leaf && leaf[0] && leaf[0].parent) {
          const linkLength = leaf[0].parent.length();
          const linkStart = range.index - leaf[1];
          this.activeLinkRange = { index: linkStart, length: linkLength };
          this.activeLinkUrl = linkFormat;
          this.savedSelection = this.activeLinkRange;

          const bounds = quill.getBounds(range.index, 1);
          const wrapperRect = this.editorWrapper.nativeElement.getBoundingClientRect();
          const editorRect = quill.container.getBoundingClientRect();
          this.linkToolbarStyle = {
            position: 'absolute',
            top: `${bounds.top + editorRect.top - wrapperRect.top + bounds.height + 4}px`,
            left: `${bounds.left + editorRect.left - wrapperRect.left}px`,
            'z-index': '1001',
          };
          this.showLinkToolbar.set(true);
        }
      }
    } else {
      this.showToolbar.set(false);
      this.textHighlighted.set(false);
    }
  }

  onEditorKeyDown(event: KeyboardEvent) {
    if (this._editor?.quillEditor) {
      const quill = this._editor.quillEditor;
      const selection = quill.getSelection();

      if (selection) {
        if (event.key === 'ArrowUp' && selection.index === 0) {
          this.chunkService.setChunkToFocusOn(this.chunkAboveId);
          event.preventDefault();
        } else if (event.key === 'ArrowDown' && selection.index >= quill.getLength() - 1) {
          this.chunkService.setChunkToFocusOn(this.chunkBelowId);
          event.preventDefault();
        }
      }
    }

    if (event.key === 'Enter' && this.mode === 'modify-chunk' && this._editor) {
      const quill = this._editor.quillEditor;
      const selection = quill.getSelection();
      if (selection && selection.index === quill.getLength() - 1) {
        // Additional check to avoid triggering on empty lines.
        const text = quill.getText();
        if ((text.length > 1 && text[text.length - 2] !== '\n') || text.length === 1) {
          event.preventDefault();
          this.handleNewLineAtEnd();
          return;
        }
      }
    }

    if (event.key === 'Backspace' && this.mode === 'modify-chunk' && this._editor?.quillEditor.getText().trim().length === 0 && this.chunkAboveId) {
      event.preventDefault();
      return;
    }

    if (event.key === '/') {
      if (this._editor && this._editor.quillEditor.getText().trim().length === 0) {
        this.openCreateOptions(new MouseEvent('click'));
      }
    }
  }

  onTextareaKeyDown(event: KeyboardEvent) {
    if (event.key === 'Backspace' && this.mode === 'modify-chunk' && (!this.contentToSend || this.contentToSend.trim().length === 0) && this.chunkAboveId) {
      event.preventDefault();
      this.deleteChunk();
      return;
    }
  }

  openCreateOptions(event: MouseEvent) {
    event.stopPropagation();
    const { componentInstance, ref: overlayRef } = this.overlayService.open(this.editorWrapper, CreateChunkOptionsOverlayComponent, {
      data: {
        mode: this.mode,
        canUndo: this.canUndo(),
        canRedo: this.canRedo(),
        canCreate: this.canCreate,
        creatingChunk: this.creatingChunk(),
        savingChunk: this.savingChunk(),
        saveSuccess: this.saveSuccess(),
        contentID: this.contentID,
        chunkAboveId: this.chunkAboveId,
        chunkBelowId: this.chunkBelowId,
        chunk: {
          ...this.chunk,
          insertBelowID: this.chunkAboveId,
          insertAsFirst: !this.chunkAboveId ? true : false,
        },
        context: 'create',
        triggerRef: this.editorWrapper,
      },
      positioning: 'topLeft',
      overlayHeight: 280,
    });

    componentInstance.textGenerated.pipe(take(1)).subscribe((text) => {
      this.contentToSend = text;
    });
    componentInstance.submit.pipe(take(1)).subscribe(() => this.submitChunk());
    componentInstance.uploadComplete.pipe(take(1)).subscribe(() => this.onUploadComplete());
    // componentInstance.recordingSaved.pipe(take(1)).subscribe((e) => this.onRecordingSaved(e.file, e.type));
    componentInstance.deleted.pipe(take(1)).subscribe(() => {
      this.doneEditing.emit();
      this.chunkService.setChunkToFocusOn(this.chunkAboveId);
    });

    overlayRef
      .keydownEvents()
      .pipe(
        filter((e) => {
          if (e.key === 'Escape') return true;
          if (e.key === '/') return false;
          return true;
        }),
        take(1)
      )
      .subscribe(() => this.overlayService.close());

    overlayRef.detachments().pipe(take(1)).subscribe();
  }

  onPaste(event) {}

  copyContentToClipBoard(): void {
    this.appService.copyHTMLToClipBoard(this.contentToSend);
  }

  convertToLatex(): void {
    if (!this.contentToSend || this.contentToSend.trim() === '') {
      return;
    }

    try {
      const node = parse(this.contentToSend);
      this.contentToSend = node.toTex();
    } catch (error) {
      console.error('Error converting to LaTeX:', error);
      this.toast.snackbar.open('Could not convert to LaTeX. Please check your syntax.', 'Close', { duration: 3000 });
    }
  }

  async convertSelectionToLatexChunk() {
    // 1. Validate that the editor and a valid selection exist
    if (!this._editor?.quillEditor) return;
    const quill = this._editor.quillEditor;
    const selection = quill.getSelection();
    if (!selection || selection.length === 0) return;

    // 2. Extract the plain text from the selected range
    const selectedText = quill.getText(selection.index, selection.length);
    if (!selectedText || selectedText.trim() === '') return;

    // 3. Attempt to parse the text as math and convert it to LaTeX
    let latexCode = '';
    try {
      const node = parse(selectedText);
      latexCode = node.toTex();
    } catch (error) {
      // If parsing fails (e.g., invalid math syntax), we fallback to the raw text
      console.error('Error converting to LaTeX:', error);
      latexCode = selectedText;
    }

    // 4. Determine if the user selected the ENTIRE chunk or just a portion of it
    const fullText = quill.getText();
    const isFullSelection = selectedText.trim() === fullText.trim();

    // --- CASE A: FULL SELECTION ---
    // If the whole chunk is selected, we simply change the current chunk's type to 'latex'
    if (isFullSelection) {
      if (this.mode === 'modify-chunk') {
        this.createChunkService.setSavingChunk(true);
        try {
          // Identify the current edition to update
          const editionToUpdate = this.chunkToEdit.editions.find((edition) => edition.language == this.selectedLanguage) || this.chunkToEdit.editions[0];
          editionToUpdate.content = latexCode;

          const data: any = {
            id: this.chunkToEdit._id,
            edition: editionToUpdate,
            type: 'latex',
            language: this.selectedLanguage,
            content: latexCode,
            contentID: this.contentID,
          };
          // Save the type change and content update to the server
          await firstValueFrom(this.chunkService.editChunk(data));

          // Update local state
          this.contentToSend = latexCode;
          this.lastSavedContent = latexCode;
          this.createChunkService.changeTextType('latex');

          // Reset history to prevent undoing back into a 'broken' HTML state
          quill.history.clear();
          this.updateHistoryState();

          this.createChunkService.setSaveSuccess(true);
        } catch (error) {
          console.error('Error converting chunk to latex:', error);
          this.toast.snackbar.open('Error converting chunk', 'Close', { duration: 3000 });
        } finally {
          this.createChunkService.setSavingChunk(false);
        }
      } else {
        // If in 'create' mode, just update the UI state
        this.contentToSend = latexCode;
        this.createChunkService.changeTextType('latex');
      }
      return;
    }

    // --- CASE B: PARTIAL SELECTION (SPLITTING) ---
    // If only a portion is selected, we split the chunk into 3: [Text Before] [New Latex Chunk] [Text After]

    // Capture the HTML content before and after the selection
    const beforeHtml = quill.getSemanticHTML(0, selection.index);
    const afterHtml = quill.getSemanticHTML(selection.index + selection.length, quill.getLength() - selection.index - selection.length);

    if (this.mode === 'modify-chunk') {
      this.createChunkService.setSavingChunk(true);
      try {
        // 1. Update the EXISTING chunk to contain only the 'Before' text
        const editionToUpdate = this.chunkToEdit.editions.find((edition) => edition.language == this.selectedLanguage) || this.chunkToEdit.editions[0];
        editionToUpdate.content = sanitizeQuillText(beforeHtml);

        const data: any = {
          id: this.chunkToEdit._id,
          edition: editionToUpdate,
          type: this.chunkType || 'html',
          language: this.selectedLanguage,
          content: sanitizeQuillText(beforeHtml),
          contentID: this.contentID,
        };
        await firstValueFrom(this.chunkService.editChunk(data));

        this.contentToSend = beforeHtml;
        this.lastSavedContent = beforeHtml;
        quill.history.clear();
        this.updateHistoryState();

        // 2. Prepare the parameters for the 2 NEW chunks (Latex and the remaining After text)
        this.chunkParams(false);
        const newChunks = [
          {
            ...this.chunk,
            type: 'latex',
            content: latexCode,
          },
          {
            ...this.chunk,
            type: this.chunkType || 'html',
            content: sanitizeQuillText(afterHtml),
          },
        ];

        // 3. Create the new chunks via API
        const serverResponse = await firstValueFrom(
          this.chunkService.createChunk({
            chunks: newChunks,
            insertBelowID: this.chunkToEdit._id,
          })
        );

        // 4. Update the local indexedDB cache with the new chunks
        if (serverResponse && serverResponse.length > 0) {
          const finalChunks = serverResponse.map((res) => ({ ...res.chunk, status: 'saved' }));
          await this.storageService.bulkStore('chunks', finalChunks);
          finalChunks.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));
        }

        this.createChunkService.setSaveSuccess(true);
        this.doneEditing.emit();
      } catch (error) {
        console.error('Error splitting chunk to latex:', error);
        this.toast.snackbar.open('Error splitting chunk', 'Close', { duration: 3000 });
      } finally {
        this.createChunkService.setSavingChunk(false);
      }
    } else {
      // If in 'create' mode, we optimistically create all parts of the split
      this.chunkParams(false);
      const newChunks = [
        {
          ...this.chunk,
          type: this.chunkType || 'html',
          content: sanitizeQuillText(beforeHtml),
        },
        {
          ...this.chunk,
          type: 'latex',
          content: latexCode,
        },
        {
          ...this.chunk,
          type: this.chunkType || 'html',
          content: sanitizeQuillText(afterHtml),
        },
      ].filter((c) => c.content && c.content.trim() !== '' && c.content !== '<p><br></p>' && c.content !== '<p></p>');

      this.canCreate = false;
      try {
        this.createChunkService.setCreatingChunk(true);

        // Optimistic UI update in Cache
        let optimisticChunks = await this.chunkService.createChunksInIndex(newChunks);
        optimisticChunks.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

        const chunksToSave = optimisticChunks.map((c) => ({ ...c, status: 'saving' }));
        await this.storageService.bulkUpdate('chunks', chunksToSave);
        chunksToSave.forEach((chunk) => this.chunkService.passChunkCreatedOnCacheEvent(chunk));

        // Prepare and send API payload
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const apiPayload = chunksToSave.map((chunk) => ({
          ...chunk.chunkParams,
          alternateID: chunk._id,
          _id: chunk._id,
          user: {
            id: userData.id,
            fullName: `${userData.firstName} ${userData.lastName}`,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
          },
        }));

        await firstValueFrom(
          this.chunkService.createChunk({
            chunks: apiPayload,
            insertBelowID: this.insertBelow,
          })
        );

        this.canCreate = true;
        this.contentToSend = '';
        const elementID = this.noteID ? this.noteID : this.lecture ? this.lecture._id : this.blogID;
        if (elementID) {
          this.storageService.remove('chunkCreator', [elementID]);
        }
        this.doneEditing.emit();
      } catch (error) {
        console.error('Error splitting to latex chunks:', error);
        this.toast.snackbar.open('Error creating chunks', 'Close', { duration: 3000 });
      } finally {
        this.createChunkService.setCreatingChunk(true);
      }
    }
  }

  convertToInlineFormula() {
    const quill = this._editor.quillEditor;
    const selection = quill.getSelection();
    if (!selection || selection.length === 0) return;

    const selectedText = quill.getText(selection.index, selection.length).trim();
    if (!selectedText) return;

    // Replace text with formula embed
    quill.deleteText(selection.index, selection.length, 'user');

    quill.insertEmbed(selection.index, 'formula', selectedText, 'user');

    // Move cursor after formula
    quill.setSelection(selection.index + 1, 0);
  }

  openLinkDialog(): void {
    const quill = this._editor.quillEditor;
    const selection = this.savedSelection;
    if (!selection) return;

    const savedIndex = selection.index;
    const savedLength = selection.length;

    const format = quill.getFormat(savedIndex, savedLength);
    const existingUrl = typeof format.link === 'string' ? format.link : null;

    // Apply temporary background format
    if (savedLength > 0) {
      quill.formatText(savedIndex, savedLength, 'background', '#cce0f5', 'silent');
    }

    this.linkInputUrl = existingUrl || '';
    this.isEditingLink.set(true);

    const bounds = quill.getBounds(savedIndex, savedLength);
    const wrapperRect = this.editorWrapper.nativeElement.getBoundingClientRect();
    const editorRect = quill.container.getBoundingClientRect();
    this.linkToolbarStyle = {
      position: 'absolute',
      top: `${bounds.top + editorRect.top - wrapperRect.top + bounds.height + 4}px`,
      left: `${bounds.left + editorRect.left - wrapperRect.left}px`,
      'z-index': '1001',
    };
    this.showLinkToolbar.set(true);

    setTimeout(() => {
      if (this.linkInput) {
        this.linkInput.focus();
        const inputEl = document.getElementById(this.linkInput.inputId) as HTMLInputElement;
        if (inputEl) {
          inputEl.select();
        }
      }
    }, 0);
  }

  startInlineLinkEdit(): void {
    const selection = this.activeLinkRange || this.savedSelection;
    if (!selection) return;

    const quill = this._editor.quillEditor;
    const savedIndex = selection.index;
    const savedLength = selection.length;

    if (savedLength > 0) {
      quill.formatText(savedIndex, savedLength, 'background', '#cce0f5', 'silent');
    }

    this.linkInputUrl = this.activeLinkUrl || '';
    this.isEditingLink.set(true);

    setTimeout(() => {
      if (this.linkInput) {
        this.linkInput.focus();
        const inputEl = document.getElementById(this.linkInput.inputId) as HTMLInputElement;
        if (inputEl) {
          inputEl.select();
        }
      }
    }, 0);
  }

  saveInlineLink(): void {
    const quill = this._editor.quillEditor;
    const selection = this.activeLinkRange || this.savedSelection;
    if (!selection) {
      this.cancelInlineLink();
      return;
    }

    const savedIndex = selection.index;
    const savedLength = selection.length;
    const url = this.linkInputUrl?.trim();

    if (savedLength > 0) {
      quill.formatText(savedIndex, savedLength, 'background', false, 'silent');
    }

    this.isEditingLink.set(false);
    this.showLinkToolbar.set(false);

    if (url) {
      quill.formatText(savedIndex, savedLength, 'link', url, 'user');
    } else {
      quill.formatText(savedIndex, savedLength, 'link', false, 'user');
    }
    this.contentToSend = quill.getSemanticHTML();
    this.contentChange$.next(this.contentToSend);

    setTimeout(() => {
      quill.focus();
      quill.setSelection(savedIndex, savedLength, 'silent');
    }, 0);
  }

  cancelInlineLink(): void {
    const quill = this._editor.quillEditor;
    const selection = this.activeLinkRange || this.savedSelection;

    if (selection && selection.length > 0) {
      quill.formatText(selection.index, selection.length, 'background', false, 'silent');
    }

    this.isEditingLink.set(false);
    this.showLinkToolbar.set(false);

    if (selection) {
      setTimeout(() => {
        quill.focus();
        quill.setSelection(selection.index, selection.length, 'silent');
      }, 0);
    }
  }

  removeLinkFromToolbar(): void {
    const quill = this._editor.quillEditor;
    const selection = this.activeLinkRange;
    if (!selection) return;

    const savedIndex = selection.index;
    const savedLength = selection.length;

    this.showLinkToolbar.set(false);

    quill.formatText(savedIndex, savedLength, 'link', false, 'user');
    this.contentToSend = quill.getSemanticHTML();
    this.contentChange$.next(this.contentToSend);

    setTimeout(() => {
      quill.focus();
      quill.setSelection(savedIndex, savedLength, 'silent');
    }, 0);
  }

  undo(): void {
    if (this._editor?.quillEditor) {
      this._editor.quillEditor.history.undo();
    }
  }

  redo(): void {
    if (this._editor?.quillEditor) {
      this._editor.quillEditor.history.redo();
    }
  }

  onLatexChange() {
    if (this.mode === 'modify-chunk') {
      // this.contentChange$.next(this.contentToSend);
    }
  }

  onTableChange() {
    const elementID = this.noteID ? this.noteID : this.lecture ? this.lecture._id : this.blogID;
    if (elementID) {
      this.storageService.store('chunkCreator', {
        _id: elementID,
        content: this.contentToSend,
      });
    }
    if (this.mode === 'modify-chunk') {
      this.contentChange$.next(this.contentToSend);
    }
  }

  unhighlightChunk(event?: MouseEvent) {
    event?.stopPropagation();
    if (this.mode === 'modify-chunk') {
      // this.submitChunk();
    }
    this.contentStateService.setHighlightedChunkId(null);
    this.contentService.setInsertBelowChunk(null);
    this.doneEditing.emit();
  }

  private updateHistoryState(): void {
    if (this._editor?.quillEditor?.history) {
      const history = this._editor.quillEditor.history;
      this.canUndo.set(history.stack.undo.length > 0);
      this.canRedo.set(history.stack.redo.length > 0);
    }
  }

  public focusEditorAtClickPosition(event: MouseEvent): void {
    if (!this._editor?.quillEditor) {
      return;
    }
    const quill = this._editor.quillEditor;

    const currentSelection = quill.getSelection();
    if (currentSelection && currentSelection.length > 0) {
      return;
    }

    // Ensure the editor has focus before trying to set the selection
    quill.focus();

    // Use a modern browser API to find the caret position from coordinates
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(event.clientX, event.clientY);
      if (range) {
        const textNode = range.startContainer;
        const offset = range.startOffset;

        // Check if the click was inside the editor's root element
        if (quill.root.contains(textNode)) {
          // Find the Quill Blot (internal representation) for the DOM node
          const blot = Quill.find(textNode, true);
          if (blot) {
            // Calculate the index within the Quill document
            const index = (blot as any).offset(quill.scroll) + offset;
            quill.setSelection(index, 0, 'user');
            return; // Success, we're done
          }
        }
      }
    }

    // Fallback: if caretRangeFromPoint is not supported, or fails to find a range.
    // The default focus behavior will place the cursor at the end or last known position.
    quill.focus();
  }
}
